import * as yup from 'yup';
import { compose, isNullOrUndefined } from 'global/utils/common';
import memoize from 'lodash/memoize';
import { checkRut, cleanRut, formatRut } from './rut/rut';

// composed rules
export const requiredRule = (yupInstance: any, message: string = 'El campo es requerido.') =>
  yupInstance.nullable().required(message);
const numberRule = (yupInstance: any) =>
  yupInstance.nullable().integer('Debe ser un número entero.').moreThan(-1, 'Solo se admiten números positivos.');

const fullNameRule = (yupInstance: any) =>
  yupInstance
    .matches(
      /^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,.'-]*$/,
      'Formató no permitido.',
    )
    .max(60);

export const dniRule = (yupInstance: any) =>
  yupInstance
    .test('isIntegerNumber', 'Solo se admiten números enteros.', (value: any) => {
      if (value === '' || isNullOrUndefined(value)) {
        return true;
      }
      return value?.match(/^\+?(0|[1-9]\d*)$/)?.length > 0;
    })
    .test('isPositiveNumber', 'Solo se admiten números positivos.', (value: any) => {
      if (value === '' || isNullOrUndefined(value)) {
        return true;
      }
      return +value > 0;
    })
    .test(
      'len',
      'Debe tener entre 5 y 7 dígitos.',
      (val: string) => !val || (String(val).length >= 5 && String(val).length <= 7),
    );

const emailRule = (yupInstance: any) =>
  yupInstance.test('email', 'El correo electrónico no es válido.', (value: string) =>
    /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,5})+$/.test(value?.trim()),
  );

export const required = () => requiredRule(yup.string());
export const number = () => numberRule(yup.number().typeError('Solo esta permitido numeros.'));

export const requiredNumber = () => compose(requiredRule, numberRule)(yup.number());

export const dniRequired = () => compose(requiredRule, dniRule)(yup.string());
export const dni = () => dniRule(yup.string());

export const fullNameRequired = () => compose(requiredRule, fullNameRule)(yup.string());
export const fullName = () => fullNameRule(yup.string());

export const emailRequired = () =>
  compose((yupI: any) => requiredRule(yupI, 'El correo es requerido.'), emailRule)(yup.string());
export const email = () => emailRule(yup.string());

const FILE_SIZE = 16 * 1024 * 1024;

export const validateFile = (extensions: string[] = []) =>
  yup
    .mixed()
    .required('El campo es requerido.')
    .test('fileSize', 'El archivo excede el tamaño permitido.', value => value && value.size <= FILE_SIZE)
    .test(
      'fileFormat',
      'Tipo de extensión no válida.',
      value => value && (extensions.length === 0 || extensions.includes(value.type)),
    );

export const rut = {
  checkLegalRut: memoize(value => {
    const { bodyPart } = checkRut(value);
    return !!(bodyPart && +bodyPart > 50000000);
  }),
  checkNativeRut: memoize(value => {
    const { bodyPart } = checkRut(value);
    return !!(bodyPart && +bodyPart < 50000000);
  }),
  checkRut,
  clean: cleanRut,
  format: formatRut,
};

// RUT juridico
export const validateLegalRut = yup
  .string()
  .required('El RUT es requerido.')
  .test('legal-rut', 'El RUT no es válido o no es un RUT de persona jurídica.', value => rut.checkLegalRut(value));

// RUT natural
export const validateNativeRut = yup
  .string()
  .required('El RUT es requerido.')
  .test('native-rut', 'El RUT no es válido.', value => rut.checkNativeRut(value) || rut.checkLegalRut(value));
